import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './Pagination.css'

/**
 * Pagination is the navigation root for a paginated collection. It is
 * composition-first: it owns no page state, renders no controls, and emits no
 * events. Your app owns the page state, URL/query syncing, cursors and any data
 * fetching; Nord supplies the semantic/layout primitives, the styling and the
 * `paginate()` math utility for building the page window.
 *
 * As a root it is a `navigation` landmark — it sets `role="navigation"` and a
 * default accessible label so screen-reader users can jump to it. Compose the
 * lower-level primitives inside it:
 *
 * - [Pagination Content](/components/pagination-content/) — the list row.
 * - [Pagination Item](/components/pagination-item/) — a slot in the row.
 * - [Pagination Link](/components/pagination-link/) — a page link (set `current`).
 * - [Pagination Previous](/components/pagination-previous/) — the previous control.
 * - [Pagination Next](/components/pagination-next/) — the next control.
 * - [Pagination Ellipsis](/components/pagination-ellipsis/) — the collapsed-pages marker.
 *
 * Bring your own interactive element — an `<a>` (e.g. a framework `<Link>` /
 * `<NuxtLink>`) or a `nord-button` — and own its `href`, navigation and
 * `disabled` state. See the framework adapters in the docs.
 *
 * @status draft
 * @category navigation
 * @lightDom true
 *
 * @localization label - Accessible label for the pagination navigation landmark.
 *
 * @cssprop [--n-pagination-gap=var(--n-space-xs)] - Controls the spacing between pagination controls.
 *
 * @slot - Default slot for the composed pagination primitives.
 */
@customElement('nord-pagination')
export default class Pagination extends DraftComponentMixin(LitElement) {
  protected localize = new LocalizeController<'nord-pagination'>(this)

  /**
   * Dims the pagination and disables interaction while results are loading.
   */
  @property({ reflect: true, type: Boolean }) loading = false

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)

    // As the navigation root, expose a landmark with an accessible name. Only
    // set defaults when the consumer hasn't provided their own semantics.
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'navigation')
    }
    if (!this.hasAttribute('aria-label') && !this.hasAttribute('aria-labelledby')) {
      this.setAttribute('aria-label', this.localize.term('label'))
    }
  }

  protected updated() {
    // Light DOM with no render(): reflect the loading state onto the host for
    // assistive tech, mirroring the visual dim/disable applied via CSS.
    if (this.loading) {
      this.setAttribute('aria-busy', 'true')
    }
    else {
      this.removeAttribute('aria-busy')
    }
  }

  // No render() — the composed primitives are the element's own light-DOM
  // content; we only style them and set the landmark semantics.
  protected createRenderRoot() {
    return this
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-pagination': Pagination
  }
}
